import React from 'react';
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Check } from "lucide-react";
import { Job } from "@/hooks/useJobs";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

interface JobCardProps {
  job: Job;
}

export const JobCard = React.memo(({
  job
}: JobCardProps) => {
  console.log(`Rendering JobCard for ${job.id}`);
  const defaultLogo = "https://images.unsplash.com/photo-1524230572899-a752b3835840";
  const { user } = useAuth();

  // Fetch the actual test responses count for this job using secure function
  const {
    data: responsesCount
  } = useQuery({
    queryKey: ['job-responses-count', job.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_test_response_count', { test_uuid: job.id });
      
      if (error) {
        console.error('Error fetching responses count:', error);
        return 0;
      }
      return data || 0;
    }
  });

  // Check if the current user has already submitted this test
  const { data: hasSubmitted } = useQuery({
    queryKey: ['user-test-submission', job.id, user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .from('test_responses')
        .select('id')
        .eq('test_id', job.id)
        .eq('candidate_id', user.id)
        .eq('status', 'completed')
        .limit(1);
        
      if (error) {
        console.error('Error checking test submission:', error);
        return false;
      }
      
      return data && data.length > 0;
    },
    enabled: !!user,
  });

  // Use the fetched count or fall back to the job's applicants_count
  const applicantsCount = responsesCount ?? job.applicants_count ?? 0;
  
  return (
    <TooltipProvider>
      <Link to={`/job/${job.id}`} className="block">
        <Card className="hover:shadow-md transition-shadow bg-card border-border p-4 h-[280px] rounded-sm">
          <div className="flex flex-col h-full">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 max-w-full">
                <div className="w-10 h-10 rounded-sm overflow-hidden border border-border flex-shrink-0">
                  <img src={job.organization?.logo_url || defaultLogo} alt={`${job.organization?.organization || 'Company'} logo`} className="w-full h-full object-cover" onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.src = defaultLogo;
                }} />
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <h3 className="font-semibold text-base text-foreground line-clamp-1">
                    {job.title}
                  </h3>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <span className="truncate max-w-[70%]">{job.organization?.organization || job.organization?.full_name}</span>
                    <span className="mx-1 flex-shrink-0">•</span>
                    <span className="flex-shrink-0">{applicantsCount} Applicant{applicantsCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              
              {hasSubmitted && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rounded-full bg-green-100 dark:bg-green-900 p-1.5">
                      <Check className="h-4 w-4 text-green-700 dark:text-green-300" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>You've already submitted this test</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {job.work_arrangement && <Badge variant="secondary" className="text-xs rounded-sm" style={{
              backgroundColor: 'var(--badge-full-time-background)',
              color: 'var(--badge-full-time-foreground)'
            }}>
                  {job.work_arrangement}
                </Badge>}
              {job.work_presence && <Badge variant="secondary" className="text-xs rounded-sm" style={{
              backgroundColor: 'var(--badge-on-site-background)',
              color: 'var(--badge-on-site-foreground)'
            }}>
                  {job.work_presence}
                </Badge>}
            </div>

            {job.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {job.description}
              </p>}

            <div className="flex flex-wrap gap-1.5 mb-4">
              {job.key_skills?.slice(0, 2).map((skill: string) => <Badge key={skill} variant="outline" className="text-xs rounded-sm border-secondary text-secondary hover:bg-secondary/10">
                  {skill}
                </Badge>)}
            </div>

            <div className="mt-auto flex items-center justify-between text-sm">
              <div className="font-medium text-foreground">
                {job.salary_range_min ? `${job.salary_range_min.toLocaleString()} ${job.salary_currency}` : 'Salary not specified'}
              </div>
              <div className="flex items-center text-muted-foreground text-xs">
                <Clock className="w-3.5 h-3.5 mr-1" />
                {job.created_at && formatDistanceToNow(new Date(job.created_at), {
                addSuffix: true
              })}
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </TooltipProvider>
  );
});

JobCard.displayName = 'JobCard';
